import { InAppBrowser } from '@awesome-cordova-plugins/in-app-browser'
import { useContext } from 'react'
import { AppContext } from '../../../contexts/AppContext'
import useUser from '../../../contexts/hooks/useUser'
import { useAchievementRefresh } from '../../../hooks/useAchievementRefresh'
import { trpc } from '../../../modules/trpc'

export type LinkEnodeHook = ReturnType<typeof useLinkEnode>

export const useLinkEnode = (goForward: () => void, type: 'vehicle' | 'inverter' | 'hvac') => {
	const utils = trpc.useContext()
	const { user, setUser } = useUser()
	const { isNativePlatform } = useContext(AppContext)
	const { eNode } = trpc.useContext()
	const { refreshAchievements } = useAchievementRefresh()

	const getWebRedirectPath = () => {
		if (type === 'vehicle') return '/devices/add-device/electric-car/setup'
		if (type === 'inverter') return '/devices/add-device/solar-panel/setup'
		return '/devices/add-device'
	}

	const handleSuccessfulLink = (linkingType: 'vehicle' | 'inverter' | 'hvac') => {
		if (!user) return

		goForward()
		Promise.all([utils.eNode.invalidate(), utils.objects.getEnergyReport.invalidate()])

		setUser({
			vehicleLinked: linkingType === 'vehicle' ? true : user.vehicleLinked,
			panelLinked: linkingType === 'inverter' ? true : user.panelLinked,
			hvacLinked: linkingType === 'hvac' ? true : user.hvacLinked,
		})

		setTimeout(() => {
			refreshAchievements()
		}, 1000)
	}

	const linkNative = (url: string) => {
		if (!user) return

		const browser = InAppBrowser.create(url, '_blank', 'location=yes')

		browser.on('loadstart').subscribe((value: any) => {
			if (
				value.url.includes('user+closed+session') ||
				value.url.includes('error=USER_INTERACTION')
			) {
				browser.close()
				return
			}

			if (value.url.includes('vihreaenergia') && !value.url.includes('enode')) {
				handleSuccessfulLink(type)
				browser.close()
			}
		})
	}

	const linkWeb = (url: string) => {
		window.location.href = url
	}

	const link = async () => {
		if (!user) return

		try {
			const redirectUri = isNativePlatform
				? 'https://vihreaenergia.fi/'
				: window.location.origin + getWebRedirectPath()

			const result = await eNode.user.linkDevice.fetch({
				userId: user.id,
				userHashedPid: user.hashedPid,
				vendorType: type,
				redirectUri,
			})

			if (result?.linkUrl) {
				if (isNativePlatform) {
					linkNative(result.linkUrl)
				} else {
					linkWeb(result.linkUrl)
				}
			}
		} catch (error) {
			console.error('Failed to link eNode:', error)
		}
	}

	return { link }
}
